import { useEffect, useRef } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

const SKIP_TAGS = new Set(["SCRIPT", "STYLE", "INPUT", "TEXTAREA", "SELECT", "OPTION", "CODE", "PRE", "NOSCRIPT"]);
const SKIP_RE = /^[\d\s\W]*$/;
const RTL = new Set(["ar"]);

export default function Translator() {
  const { realUser } = useAuth();
  const lang = realUser?.language || localStorage.getItem("pb_lang") || "en";
  const dict = useRef(new Map());
  const outputs = useRef(new Set());
  const pending = useRef(new Set());
  const timer = useRef(null);

  useEffect(() => {
    document.documentElement.dir = RTL.has(lang) ? "rtl" : "ltr";
    document.documentElement.lang = lang;
    if (!realUser || lang === "en") return;
    try {
      const saved = sessionStorage.getItem(`pb_i18n_${lang}`);
      if (saved) {
        dict.current = new Map(JSON.parse(saved));
        dict.current.forEach((v) => outputs.current.add(v.trim()));
      }
    } catch {}

    const collect = (root, bag) => {
      const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
        acceptNode: (n) => {
          const p = n.parentElement;
          if (!p || SKIP_TAGS.has(p.tagName) || p.isContentEditable || p.closest("[data-no-translate]")) return NodeFilter.FILTER_REJECT;
          const t = n.nodeValue;
          if (!t || !t.trim() || t.trim().length > 400 || SKIP_RE.test(t)) return NodeFilter.FILTER_REJECT;
          return NodeFilter.FILTER_ACCEPT;
        },
      });
      let node;
      while ((node = walker.nextNode())) bag.push(node);
    };

    const apply = (nodes) => {
      const missing = new Set();
      nodes.forEach((n) => {
        const key = n.nodeValue.trim();
        const tr = dict.current.get(key);
        if (tr) {
          if (tr !== key) n.nodeValue = n.nodeValue.replace(key, tr);
        } else missing.add(key);
      });
      missing.forEach((k) => pending.current.add(k));
      if (pending.current.size) schedule();
    };

    const schedule = () => {
      clearTimeout(timer.current);
      timer.current = setTimeout(flush, 350);
    };

    const flush = async () => {
      const batch = [...pending.current].slice(0, 120);
      if (!batch.length) return;
      batch.forEach((k) => pending.current.delete(k));
      try {
        const { data } = await api.post("/i18n/translate", { lang, strings: batch });
        Object.entries(data.translations).forEach(([src, tr]) => {
          dict.current.set(src.trim(), tr);
          outputs.current.add(String(tr).trim());
        });
        try { sessionStorage.setItem(`pb_i18n_${lang}`, JSON.stringify([...dict.current])); } catch {}
        const bag = [];
        collect(document.body, bag);
        bag.forEach((n) => {
          const key = n.nodeValue.trim();
          const tr = dict.current.get(key);
          if (tr && tr !== key) n.nodeValue = n.nodeValue.replace(key, tr);
        });
      } catch {}
      if (pending.current.size) schedule();
    };

    const run = () => {
      const bag = [];
      collect(document.body, bag);
      apply(bag);
    };
    run();
    const obs = new MutationObserver((muts) => {
      const bag = [];
      muts.forEach((m) => {
        m.addedNodes.forEach((n) => {
          if (n.nodeType === Node.TEXT_NODE) bag.push(n);
          else if (n.nodeType === Node.ELEMENT_NODE && !SKIP_TAGS.has(n.tagName)) collect(n, bag);
        });
        if (m.type === "characterData" && m.target.nodeType === Node.TEXT_NODE) bag.push(m.target);
      });
      if (bag.length) apply(bag);
    });
    obs.observe(document.body, { childList: true, subtree: true, characterData: true });
    return () => { obs.disconnect(); clearTimeout(timer.current); };
  }, [lang, realUser]);

  return null;
}
